// How a client reaches Estra: HTTP to a Worker, or an in-process client (tests, server code).
import type { CommitRequest, DocOut, Query } from "@estra/schema";
import { realtimeConnection, type Listen } from "./realtime.ts";

export interface CommitOut {
  writes: { path: string; v: number | null; seq: number }[];
  seq: number;
  duplicate?: boolean;
}
export interface QueryOut {
  docs: DocOut[];
  hasMore: boolean;
  truncated?: boolean;
  fromCache?: boolean;
}

export interface Transport {
  /** Live listeners (onSnapshot); absent = not supported by this transport. */
  listen?: Listen;
  getAll(paths: string[]): Promise<(DocOut | null)[]>;
  query(query: Query): Promise<QueryOut>;
  aggregate(query: Query): Promise<Record<string, number | null>>;
  commit(req: CommitRequest): Promise<CommitOut>;
  /** Hot counters (POST /v1/counters/inc): buffered server-side, flushed about once a second. */
  counterInc?(path: string, field: string, by: number): Promise<{ value: number | null }>;
  /** Re-send the auth token on the live connection now (after sign-in / sign-out). */
  refreshAuth?(): void;
}

/** Errors carry Firestore's code names ("permission-denied", "not-found" …). */
export class EstraClientError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = "EstraError";
  }
}

export interface HttpOptions {
  /** Base URL of the app's Estra Worker, e.g. https://api.example.com */
  url: string;
  /** Current access token, or null when signed out. */
  getToken?: () => Promise<string | null> | string | null;
  fetch?: typeof fetch;
  /** WebSocket implementation for onSnapshot (defaults to the global one; false = no listeners). */
  WebSocket?: typeof WebSocket | false;
}

export function httpTransport(o: HttpOptions): Transport {
  const f = o.fetch ?? fetch;
  const call = async <T>(route: string, body: unknown): Promise<T> => {
    const token = o.getToken ? await o.getToken() : null;
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (token) headers.authorization = `Bearer ${token}`;
    let r: Response;
    try {
      r = await f(`${o.url.replace(/\/$/, "")}/v1/${route}`, { method: "POST", headers, body: JSON.stringify(body) });
    } catch {
      throw new EstraClientError("unavailable", "network request failed");
    }
    if (r.status === 502 || r.status === 503 || r.status === 504) throw new EstraClientError("unavailable", `HTTP ${r.status}`);
    const j = (await r.json().catch(() => ({}))) as { error?: { code: string; message: string } } & T;
    if (!r.ok) throw new EstraClientError(j.error?.code ?? "unavailable", j.error?.message ?? `HTTP ${r.status}`);
    return j;
  };
  let rt: ReturnType<typeof realtimeConnection> | null = null;
  const listen: Listen | undefined =
    o.WebSocket === false
      ? undefined
      : (target, h) => (rt ??= realtimeConnection({ url: o.url, ...(o.getToken ? { getToken: o.getToken } : {}), ...(o.WebSocket ? { WebSocket: o.WebSocket } : {}) })).listen(target, h);
  return {
    ...(listen ? { listen } : {}),
    refreshAuth: () => rt?.refreshAuth(),
    getAll: async (paths) => (await call<{ docs: (DocOut | null)[] }>("get", { paths })).docs,
    query: (query) => call<QueryOut>("query", { query }),
    aggregate: async (query) => (await call<{ aggregate: Record<string, number | null> }>("query", { query })).aggregate,
    commit: (req) => call<CommitOut>("commit", req),
    counterInc: (path, field, by) => call<{ value: number | null }>("counters/inc", { path, field, by }),
  };
}
